import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {VwuiModalConfig, VwuiModalRef} from '@bvgo-ict/vwui-angular';
import {ProjectDetails} from '../../models';
import {CompanyTypeService, ProjectService, ToastService} from '../../services';
import {TranslateService} from '@ngx-translate/core';
import {ObjectUtil} from '../../utils';
import {ICompanyType, IUserIdentity} from '../../models/user/user';
import {IProjectMember, ProjectMember} from '../../models/project/project';
import {BehaviorSubject} from 'rxjs';

@Component({
    selector: 'app-share-project-modal',
    templateUrl: './share-project-modal.component.html',
    styleUrls: ['./share-project-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ShareProjectModalComponent {
    public modalRef = inject(VwuiModalRef);
    public  modalConfig = inject<VwuiModalConfig<{ projectDetails: ProjectDetails; }>>(VwuiModalConfig);
    private projectService = inject(ProjectService);
    private toastService = inject(ToastService);
    private companyTypeService = inject(CompanyTypeService);
    private translateService = inject(TranslateService);


    public form = new UntypedFormGroup({
        user: new UntypedFormControl(null)
    });

    public saving = false;

    private members: IProjectMember[];
    public projectDetails: ProjectDetails;
    public companyType: ICompanyType = null;
    public identitiesToExclude$: BehaviorSubject<IUserIdentity[]> = new BehaviorSubject<IUserIdentity[]>([]);

    constructor() {
        const modalConfig = this.modalConfig;

        this.members = ObjectUtil.cloneObject(modalConfig.data.projectDetails.members);
        this.projectDetails = modalConfig.data.projectDetails;
        this.updateExclusionList();

        this.companyTypeService.getCompanyType(this.projectDetails.companyTypeId).then((companyType) => {
            this.companyType = companyType;
        });
    }

    public submit() {
        this.saving = true;
        this.projectDetails.members = this.members.map((iProjectMember) => new ProjectMember(iProjectMember));
        this.projectService.saveProjectDetails(this.projectDetails).then((newDetails) => {
            const message = this.translateService.instant('COMPONENT.share_project.succeeded');
            this.toastService.openSuccess(message);
            this.modalRef.close(newDetails);
        }).catch((error) => {
            this.toastService.handleError(error, 'ERRORS.project_sharing_failed');
        }).finally(() => {
            this.saving = false;
        });
    }

    public getMembers(): IProjectMember[] {
        return this.members;
    }

    public removeMember(member: IProjectMember): void {
        const index = this.members.indexOf(member, 0);
        if (index > -1) {
            this.members.splice(index, 1);
        }
        this.updateExclusionList();
    }

    public addMember() {
        const user = this.form.value.user;
        if (this.form.valid && user) {
            this.members.push({
                user,
                quickscanEditor: false,
                projectLibraryItemEditor: false,
                tisEditor: false,
                hasViewRights: true
            });
            this.form.reset();
            this.updateExclusionList();
        }
    }

    public isAddMemberButtonDisabled(): boolean {
        return !(this.form.valid && this.form.value.user);
    }

    public toggleQuickscan($event: MouseEvent, member: IProjectMember) {
        $event.preventDefault();
        member.quickscanEditor = !member.quickscanEditor;
    }

    public toggleProjectLibraryItems($event: MouseEvent, member: IProjectMember) {
        $event.preventDefault();
        member.projectLibraryItemEditor = !member.projectLibraryItemEditor;
    }

    public toggleTis($event: MouseEvent, member: IProjectMember) {
        $event.preventDefault();
        member.tisEditor = !member.tisEditor;
    }

    private updateExclusionList(): void {
        const identitiesToExclude = [this.projectDetails.owner];
        this.members.forEach((member) => identitiesToExclude.push(member.user));
        this.identitiesToExclude$.next(identitiesToExclude);
    }
}
